import React, { useEffect } from "react";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Flex } from "@amzn/stencil-react-components/layout";
import useArb from "../../hooks/use-arb";
import {
  getCurrentPage,
  getDocWidgets,
  getIsSubmitButtonEnabled,
  setActivePage,
  toggleSubmitButton,
} from "../../reduxStore/slices/docUploadSlice";
import { DOC_UPLOAD_PAGE_STATE } from "../../utility/enums/common";
import "./index.style.scss";
import { useAppDispatch, useAppSelector } from "../../reduxStore/reduxHooks";
import { useCommitDocumentsMutation } from "../../reduxStore/api/apiSlice";
import { getValidEdmPreCommitPayload } from "../../helpers/utils";

interface FlyoutButtonsProps {
  closeFlyoutHandler: () => void;
}

export const FlyoutButtons = ({ closeFlyoutHandler }: FlyoutButtonsProps) => {
  const { getMessage } = useArb("HVHChecklistPantherResource");
  const dispatch = useAppDispatch();
  const isSubmitButtonEnabled = useAppSelector(getIsSubmitButtonEnabled);
  const currentPage = useAppSelector(getCurrentPage);
  const docs = useAppSelector(getDocWidgets);

  const [commitDocumentsToEDM] = useCommitDocumentsMutation();

  const isEligibleToCommitDocsToEDM = docs.length > 0 && docs.filter((doc) => !doc.markForDeletion).length > 0;

  useEffect(() => {
    if (currentPage === DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE && isEligibleToCommitDocsToEDM) {
      dispatch(toggleSubmitButton({ isEnabled: true }));
    } else {
      dispatch(toggleSubmitButton({ isEnabled: false }));
    }
  }, [currentPage, docs]);

  const handleSaveToEdm = () => {
    if (!isSubmitButtonEnabled) return;

    if (currentPage === DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE) {
      const documents = getValidEdmPreCommitPayload(docs);
      // TODO: Change the payload into real data when the API ready
      commitDocumentsToEDM({ applicationId: "applicationId", candidateGlobalId: "candidateGlobalId", documents });
    } else if (currentPage === DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD) {
      // call presigned url to save on select file to upload & update docsWidget on Redux
      dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE }));
    }
  };

  const handleCloseFlyout = () => {
    if (currentPage === DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD) {
      dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE }));
    } else {
      closeFlyoutHandler();
    }
  };

  return (
    <Flex flexDirection="row" gridGap="S300" className="flyout-container">
      <Button variant={ButtonVariant.Tertiary} onClick={handleCloseFlyout}>
        {getMessage("Checklist-Common-Cancel", "Cancel")}
      </Button>
      <Button
        variant={ButtonVariant.Primary}
        disabled={!isSubmitButtonEnabled}
        data-testid={`save-disabled=${!isSubmitButtonEnabled}`}
        onClick={handleSaveToEdm}
      >
        {getMessage("Checklist-Common-Save", "Save")}
      </Button>
    </Flex>
  );
};
